import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Search,
  X,
} from 'lucide-react';

import {
  memberService,
} from '../../member/services/memberService';

import type {
  MemberInfo,
} from '../../member/types/member.types';

import {
  useModal,
} from '../../../shared/components/ModalProvider';

interface AddPlayerModalProps {
  isOpen: boolean;

  onClose: () => void;

  onAddPlayer: (
    userId: number,
  ) => Promise<void>;

  existingPlayerIds: number[];
}

const AddPlayerModal:
  React.FC<
    AddPlayerModalProps
  > = ({
    isOpen,
    onClose,
    onAddPlayer,
    existingPlayerIds,
  }) => {
  const modal =
    useModal();

  const [
    users,
    setUsers,
  ] =
    useState<MemberInfo[]>(
      [],
    );

  const [
    searchTerm,
    setSearchTerm,
  ] = useState('');

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    selectedUserId,
    setSelectedUserId,
  ] =
    useState<number | null>(
      null,
    );

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);

  /**
   * 회원 목록 조회
   */
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let isActive =
      true;

    const loadUsers =
      async (): Promise<void> => {
        try {
          setLoading(
            true,
          );

          const data =
            await memberService
              .getMembers();

          if (!isActive) {
            return;
          }

          setUsers(
            data,
          );
        } catch (
          error: unknown
        ) {
          console.error(
            '회원 목록 조회 실패:',
            error,
          );

          if (
            isActive
          ) {
            setUsers(
              [],
            );

            await modal.alert(
              '회원 목록을 불러오는데 실패했습니다.',
            );
          }
        } finally {
          if (
            isActive
          ) {
            setLoading(
              false,
            );
          }
        }
      };

    void loadUsers();

    return () => {
      isActive = false;
    };
  }, [
    isOpen,
    modal,
  ]);

  /**
   * 모달이 닫혔을 때
   * 내부 상태 초기화
   */
  useEffect(() => {
    if (isOpen) {
      return;
    }

    setSearchTerm(
      '',
    );

    setSelectedUserId(
      null,
    );

    setIsSubmitting(
      false,
    );
  }, [isOpen]);

  /**
   * 참가 가능한 회원 필터
   */
  const filteredUsers =
    useMemo(() => {
      const search =
        searchTerm
          .trim()
          .toLowerCase();

      return users.filter(
        (user) => {
          /**
           * 이미 게임에 참가한 회원 제외
           */
          if (
            existingPlayerIds.includes(
              user.id,
            )
          ) {
            return false;
          }

          /**
           * 비활성/탈퇴/정지 회원은
           * 플레이어 추가 대상에서 제외
           */
          if (
            user.status !==
            'ACTIVE'
          ) {
            return false;
          }

          if (!search) {
            return true;
          }

          const userName =
            user.userName
              ?.toLowerCase() ??
            '';

          const userId =
            user.userId
              ?.toLowerCase() ??
            '';

          const userNickname =
            user.userNickname
              ?.toLowerCase() ??
            '';

          const phoneNumber =
            user.phoneNumber
              ?.toLowerCase() ??
            '';

          return (
            userName.includes(
              search,
            ) ||
            userId.includes(
              search,
            ) ||
            userNickname.includes(
              search,
            ) ||
            phoneNumber.includes(
              search,
            )
          );
        },
      );
    }, [
      users,
      existingPlayerIds,
      searchTerm,
    ]);

  /**
   * 플레이어 추가
   */
  const handleSubmit =
    async (): Promise<void> => {
      if (
        selectedUserId ===
        null
      ) {
        return;
      }

      try {
        setIsSubmitting(
          true,
        );

        await onAddPlayer(
          selectedUserId,
        );

        setSelectedUserId(
          null,
        );

        setSearchTerm(
          '',
        );

        onClose();
      } catch (
        error: unknown
      ) {
        /**
         * 실제 추가 실패 처리는
         * 부모 컴포넌트에서 담당
         */
        console.error(
          '플레이어 추가 실패:',
          error,
        );
      } finally {
        setIsSubmitting(
          false,
        );
      }
    };

  /**
   * 모달 닫기
   */
  const handleClose =
    (): void => {
      if (
        isSubmitting
      ) {
        return;
      }

      setSelectedUserId(
        null,
      );

      setSearchTerm(
        '',
      );

      onClose();
    };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="플레이어 추가"
      onMouseDown={(
        event,
      ) => {
        if (
          event.target ===
          event.currentTarget
        ) {
          handleClose();
        }
      }}
    >
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-gray-200 p-5 sm:p-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
              플레이어 추가
            </h2>

            <p className="mt-1 text-xs text-gray-500 sm:text-sm">
              게임에 추가할 회원을
              선택해주세요.
            </p>
          </div>

          <button
            type="button"
            onClick={
              handleClose
            }
            disabled={
              isSubmitting
            }
            className="grid h-9 w-9 place-items-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="플레이어 추가 닫기"
          >
            <X
              size={22}
            />
          </button>
        </header>

        {/* Search */}
        <section className="border-b border-gray-200 p-4 sm:p-6">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />

            <input
              type="search"
              placeholder="이름, 닉네임, 아이디, 전화번호 검색"
              value={
                searchTerm
              }
              onChange={(
                event,
              ) =>
                setSearchTerm(
                  event.target
                    .value,
                )
              }
              disabled={
                isSubmitting
              }
              className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-4 text-sm outline-none transition focus:border-main focus:ring-2 focus:ring-main/10 disabled:bg-gray-100 sm:text-base"
            />
          </div>
        </section>

        {/* Member List */}
        <section className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex min-h-48 items-center justify-center">
              <p className="text-sm text-gray-600 sm:text-base">
                로딩 중...
              </p>
            </div>
          ) : filteredUsers.length ===
            0 ? (
            <div className="flex min-h-48 items-center justify-center">
              <div className="text-center">
                <p className="text-sm text-gray-600 sm:text-base">
                  {searchTerm.trim()
                    ? '검색 결과가 없습니다.'
                    : '추가 가능한 회원이 없습니다.'}
                </p>

                {!searchTerm.trim() && (
                  <p className="mt-2 text-xs text-gray-500 sm:text-sm">
                    이미 참가했거나 활성 상태가 아닌 회원은 표시되지 않습니다.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredUsers.map(
                (user) => {
                  const isSelected =
                    selectedUserId ===
                    user.id;

                  return (
                    <button
                      key={
                        user.id
                      }
                      type="button"
                      onClick={() =>
                        setSelectedUserId(
                          user.id,
                        )
                      }
                      disabled={
                        isSubmitting
                      }
                      className={[
                        'w-full rounded-xl border p-4 text-left transition-all',

                        'disabled:cursor-not-allowed disabled:opacity-50',

                        isSelected
                          ? 'border-main bg-main/5 ring-1 ring-main'
                          : 'border-gray-300 hover:border-main hover:bg-gray-50',
                      ].join(
                        ' ',
                      )}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <p className="truncate text-base font-semibold text-gray-900 sm:text-lg">
                            {
                              user.userName
                            }
                          </p>

                          <p className="mt-1 truncate text-sm text-gray-600 sm:text-base">
                            #
                            {user.userNickname ||
                              user.userId}
                            {' · '}
                            {user.phoneNumber ??
                              '전화번호 없음'}
                          </p>

                          <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                            라포이트 시드:{' '}
                            {(user.point ??
                              0).toLocaleString(
                                'ko-KR',
                              )}
                          </p>
                        </div>

                        {isSelected && (
                          <span className="shrink-0 text-sm font-semibold text-main">
                            선택됨
                          </span>
                        )}
                      </div>
                    </button>
                  );
                },
              )}
            </div>
          )}
        </section>

        {/* Footer */}
        <footer className="border-t border-gray-200 p-4 sm:p-6">
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={
                handleClose
              }
              disabled={
                isSubmitting
              }
              className="rounded-lg bg-gray-200 px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
            >
              취소
            </button>

            <button
              type="button"
              onClick={() =>
                void handleSubmit()
              }
              disabled={
                isSubmitting ||
                selectedUserId ===
                  null
              }
              className="rounded-lg bg-main px-4 py-3 text-sm font-medium text-white transition hover:bg-main-medium disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
            >
              {isSubmitting
                ? '추가 중...'
                : '추가'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AddPlayerModal;